import { PropsWithChildren } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { FileSizeOptions } from 'filesize/types/filesize';

import { bytesToReadableFormat } from '@/react/kubernetes/utils';

import { InlineLoader } from '@@/InlineLoader';

import { MetricSeriesPoint } from '../../queries/useResourceMetrics';

export type MetricUnit = 'percent' | 'bytes';

export interface MetricCardSeries {
  name: string;
  color: string;
  data: MetricSeriesPoint[];
}

interface Props {
  /** Omit when nesting inside a Widget/Widget.Title that already provides a border and title. */
  title?: string;
  unit: MetricUnit;
  series: MetricCardSeries[];
  /** Show a loading placeholder while the underlying query is fetching for the first time. */
  isLoading?: boolean;
  /** Show an error placeholder when the underlying query failed. */
  isError?: boolean;
}

export const DEFAULT_SERIES_COLOR = '#875bf7';

// CHART_HEIGHT keeps the chart and every placeholder state (loading, error,
// empty) the same height, so cards don't jump as their queries resolve.
const CHART_HEIGHT = 140;

const BYTES_FORMAT_OPTIONS: FileSizeOptions = {
  output: 'string',
  standard: 'iec',
  round: 1,
};

export function MetricCard({ title, unit, series, isLoading, isError }: Props) {
  const data = mergeSeries(series);
  const spansMultipleDays = dataSpansMultipleDays(data);

  let chart;
  if (isError) {
    chart = <ChartPlaceholder>Failed to load</ChartPlaceholder>;
  } else if (isLoading) {
    chart = (
      <ChartPlaceholder>
        <InlineLoader>Loading...</InlineLoader>
      </ChartPlaceholder>
    );
  } else if (data.length === 0) {
    chart = <ChartPlaceholder>No data yet</ChartPlaceholder>;
  } else {
    chart = (
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <LineChart data={data} margin={{ top: 4, right: 4, left: 4 }}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--border-color, #e1e0d9)"
          />
          <XAxis
            dataKey="time"
            tickFormatter={(time) =>
              formatTimeTick(String(time), spansMultipleDays)
            }
            tick={{ fontSize: 11, fill: 'var(--text-muted-color, #898781)' }}
            axisLine={{ stroke: 'var(--border-color, #e1e0d9)' }}
            tickLine={false}
            minTickGap={30}
          />
          <YAxis
            tickFormatter={(value) => formatAxisValue(Number(value), unit)}
            tick={{ fontSize: 11, fill: 'var(--text-muted-color, #898781)' }}
            axisLine={{ stroke: 'var(--border-color, #e1e0d9)' }}
            tickLine={false}
            width={56}
          />
          <Tooltip
            formatter={(value) => formatValue(Number(value), unit)}
            labelFormatter={(time) =>
              formatTimeTick(String(time), spansMultipleDays)
            }
          />
          {series.length > 1 && (
            <Legend wrapperStyle={{ fontSize: 12 }} iconType="plainline" />
          )}
          {series.map((s) => (
            <Line
              key={s.name}
              type="monotone"
              dataKey={s.name}
              name={s.name}
              stroke={s.color}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  }

  if (!title) {
    return chart;
  }

  return (
    <div className="rounded-lg border border-gray-5 bg-white p-4 th-highcontrast:border-white th-highcontrast:bg-black th-dark:border-gray-9 th-dark:bg-gray-11">
      <div className="mb-2 text-sm font-medium text-gray-8 th-highcontrast:text-white th-dark:text-gray-5">
        {title}
      </div>
      {chart}
    </div>
  );
}

// ChartPlaceholder centers a short message in the same footprint the chart
// occupies, so loading, error, and empty states share the chart's height.
function ChartPlaceholder({ children }: PropsWithChildren<unknown>) {
  return (
    <div
      className="flex items-center justify-center text-sm text-gray-6 th-highcontrast:text-gray-4 th-dark:text-gray-7"
      style={{ height: CHART_HEIGHT }}
    >
      {children}
    </div>
  );
}

// mergeSeries combines multiple named series into one row-per-timestamp
// dataset Recharts can plot as several <Line>s sharing an x-axis. Series
// don't necessarily share exact timestamps (e.g. independent rx/tx
// queries), so rows are keyed by time and merged rather than zipped by index.
function mergeSeries(series: MetricCardSeries[]): Record<string, unknown>[] {
  const rowByTime = new Map<string, Record<string, unknown>>();

  for (const s of series) {
    for (const point of s.data) {
      const row = rowByTime.get(point.time) ?? { time: point.time };
      row[s.name] = point.value;
      rowByTime.set(point.time, row);
    }
  }

  return Array.from(rowByTime.values()).sort((a, b) =>
    String(a.time).localeCompare(String(b.time))
  );
}

// dataSpansMultipleDays reports whether the chart's data covers more than
// one calendar day, so formatTimeTick knows whether time-of-day alone would
// be ambiguous (the same tick labels appearing for any date range).
function dataSpansMultipleDays(data: Record<string, unknown>[]) {
  if (data.length === 0) {
    return false;
  }
  const first = new Date(String(data[0].time));
  const last = new Date(String(data[data.length - 1].time));
  return first.toDateString() !== last.toDateString();
}

function formatTimeTick(time: string, includeDate: boolean) {
  const date = new Date(time);
  const timePart = date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
  if (!includeDate) {
    return timePart;
  }
  const datePart = date.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
  });
  return `${datePart}, ${timePart}`;
}

function formatValue(value: number, unit: MetricUnit) {
  return unit === 'percent'
    ? `${formatPercent(value)}%`
    : String(bytesToReadableFormat(value, BYTES_FORMAT_OPTIONS));
}

function formatAxisValue(value: number, unit: MetricUnit) {
  return unit === 'percent'
    ? `${formatPercent(value)}%`
    : String(bytesToReadableFormat(value, BYTES_FORMAT_OPTIONS));
}

// formatPercent shows one decimal by default, but keeps two for small
// values (e.g. CPU usage often sits well under 1%) so the chart doesn't
// collapse every reading to "0.0%".
function formatPercent(value: number) {
  if (value !== 0 && Math.abs(value) < 1) {
    return value.toFixed(2);
  }
  return value.toFixed(1);
}
